<template>
  <component
    :is="as"
    :class="[
      weight && weights[weight],
      {
        [sizes[size]]: size,
        [colors[color]]: color,
        [textStyles[textStyle]]: textStyle,
        [whitespaces[whitespace]]: whitespace,
      },
    ]"
  >
    <slot />
  </component>
</template>

<script setup lang="ts">
type TextType = 'span' | 'div' | 'p' | 'label' | 'a'
type TextStyle = 'uppercase' | 'lowercase' | 'capitalize' | 'normal-case'
type TextSize = 'xxs' | 'xs' | 'md' | 'sm' | 'lg' | 'xl' | 'xxl' | 'xxxl'
type TextWeight =
  | 'extralight'
  | 'light'
  | 'regular'
  | 'medium'
  | 'semibold'
  | 'bold'
  | 'extrabold'
  | 'black'
type TextColors =
  | 'primary'
  | 'primaryGray'
  | 'primaryBlack'
  | 'accent'
  | 'secondary'
  | 'grayLight'
  | 'error'
  | 'warning'
  | 'black'
  | 'white'
  | 'inherit'
  | 'success'
  | 'none'
type TextWhitespace =
  | 'normal'
  | 'nowrap'
  | 'pre'
  | 'pre-line'
  | 'pre-wrap'
  | 'truncate'
  | 'break-spaces'

withDefaults(
  defineProps<{
    as?: TextType
    size?: TextSize
    weight?: TextWeight
    textStyle?: TextStyle
    color?: TextColors
    whitespace?: TextWhitespace
  }>(),
  {
    as: 'span',
    size: 'md',
    weight: undefined,
    color: 'secondary',
    textStyle: 'normal-case',
    whitespace: 'normal',
  }
)

const sizes: Record<TextSize, string> = {
  xxs: 'tw-text-xxs',
  xs: 'tw-text-xs',
  sm: 'tw-text-sm',
  md: 'tw-text-base',
  lg: 'tw-text-lg',
  xl: 'tw-text-xl',
  xxl: 'tw-text-2xl',
  xxxl: 'tw-text-3xl',
}

const weights: Record<TextWeight, string> = {
  extralight: 'tw-font-extralight',
  light: 'tw-font-light',
  regular: 'tw-font-normal',
  medium: 'tw-font-medium',
  semibold: 'tw-font-semibold',
  bold: 'tw-font-bold',
  extrabold: 'tw-font-extrabold',
  black: 'tw-font-black',
}

const colors: Record<TextColors, string> = {
  primary: 'tw-text-blue-500',
  accent: 'tw-text-blue-400',
  secondary: 'tw-text-gray-3',
  warning: 'tw-text-orange-500',
  grayLight: 'tw-text-gray-light',
  primaryGray: 'tw-text-primary-gray',
  primaryBlack: 'tw-text-primary-black',
  black: 'tw-text-black',
  error: 'tw-text-red-500',
  white: 'tw-text-white',
  inherit: 'tw-text-inherit',
  success: 'tw-text-green-500',
  none: '',
}

const textStyles: Record<TextStyle, string> = {
  uppercase: 'tw-uppercase',
  lowercase: 'tw-lowercase',
  capitalize: 'tw-capitalize',
  'normal-case': 'tw-normal-case',
}

const whitespaces: Record<TextWhitespace, string> = {
  normal: 'tw-whitespace-normal',
  nowrap: 'tw-whitespace-nowrap',
  pre: 'tw-whitespace-pre',
  'pre-line': 'tw-whitespace-pre-line',
  'pre-wrap': 'tw-whitespace-pre-wrap',
  'break-spaces': 'tw-whitespace-break-spaces',
  truncate: 'tw-truncate',
}
</script>
